import {Component, Input, OnChanges} from '@angular/core';
import {CptCategories, CptSummary, Legend} from '../../../shared/models';
import {select} from '@angular-redux/store';
import {Observable} from 'rxjs';
import {
  getDefaultCptSummary,
  getMaxYaxisValueForEAndMGraphWithExtendedBenchmarkOptions,
  getMaxYaxisValueForEAndMGraphWithOriginalBenchmarkOptions
} from '../../em-helpers';
import {BenchmarkOption} from '../../../shared/enums';
import {getEmLegends} from '../../em-chart-helpers';
import {MetricType} from '../../../shared/metric-types';

@Component({
  selector: 'app-em-outpatient-multilevel-charts',
  templateUrl: './em-outpatient-multilevel-charts.component.html',
  styleUrls: ['./em-outpatient-multilevel-charts.component.scss']
})
export class EmOutpatientMultilevelChartsComponent implements OnChanges {
  @Input() totalCptCounts?: CptCategories;
  @Input() showProgressBar: boolean;
  @Input() specialtyCptCounts?: CptCategories;
  @Input() isSingleProviderSelected: boolean;
  @Input() chartWidth: number;
  @Input() chartHeight: number;
  @Input() overviewPageView: boolean;
  @Input() foundNodeName: string;
  @Input() showExtendedBenchmarkOptions: boolean;
  @Input() viewCommunityBenchmarks: boolean;
  @Input() benchmarkOption: BenchmarkOption;
  @Input() fromPdf: boolean;

  @select(['display', 'isProviderSelected'])
  private readonly isProviderSelected$: Observable<boolean>;
  overviewPageChartHeight = 250;
  totalNewPatientsCptSummaries: (CptSummary | undefined)[];
  totalEstablishedPatientsCptSummaries: (CptSummary | undefined)[];
  totalConsultationsCptSummaries: (CptSummary | undefined)[];
  specialtyTotalNewPatientsCptSummaries: (CptSummary | undefined)[];
  specialtyTotalEstablishedPatientsCptSummaries: (CptSummary | undefined)[];
  specialtyTotalConsultationsCptSummaries: (CptSummary | undefined)[];
  readonly newPatientVisitsCategories = ['99201', '99202', '99203', '99204', '99205'];
  readonly establishedPatientVisitsCategories = ['99211', '99212', '99213', '99214', '99215'];
  readonly consultationsCategories = ['99241', '99242', '99243', '99244', '99245'];
  newPatientsMaxValue = 0;
  establishedPatientsMaxValue = 0;
  consultationsMaxValue = 0;
  specialtyNewPatientsMaxValue = 0;
  specialtyEstablishedPatientsMaxValue = 0;
  specialtyConsultationsMaxValue = 0;
  maxNewPatientYAxisValue: number;
  maxEstablishedPatientPatientYAxisValue: number;
  maxConsultationsYAxisValue: number;
  legends: Legend[];
  metricType = MetricType.EandMOutpatient;

  ngOnChanges(): void {
    this.chartHeight = this.overviewPageView ? this.overviewPageChartHeight : this.chartHeight;
    this.legends = getEmLegends(this.foundNodeName, this.isSingleProviderSelected);

    if (this.totalCptCounts) {
      this.specialtyCptCounts ? this.processSpecialtyCountTotals() : this.createBlankSpecialtyCptCounts();
      this.processCountTotals();
      this.setYAxesMaxValues();
    } else {
      this.createBlankCptSummaries();
    }
  }

  private processCountTotals() {
    this.processNewPatientVisitTotals();
    this.processEstablishedPatientVisitTotals();
    this.processConsultationTotals();
  }

  private processSpecialtyCountTotals() {
    this.processSpecialtyNewPatientVisitTotals();
    this.processSpecialtyEstablishedPatientVisitTotals();
    this.processSpecialtyConsultationTotals();
  }

  private processSpecialtyConsultationTotals() {
    if (this.specialtyCptCounts?.consultation) {
      this.updateSpecialtyTotalConsultationsCptSummaries();
      this.specialtyConsultationsMaxValue = this.calculateMaxYValue(this.specialtyTotalConsultationsCptSummaries);
    }
  }

  private processConsultationTotals() {
    if (this.totalCptCounts?.consultation) {
      this.updateTotalConsultationsCptSummaries();
      this.consultationsMaxValue = this.calculateMaxYValue(this.totalConsultationsCptSummaries);
    }
  }

  private processSpecialtyEstablishedPatientVisitTotals() {
    if (this.specialtyCptCounts?.establishedPatientVisit) {
      this.updateSpecialtyTotalEstablishedPatientsCptSummaries();
      this.specialtyEstablishedPatientsMaxValue = this.calculateMaxYValue(this.specialtyTotalEstablishedPatientsCptSummaries);
    }
  }

  private processEstablishedPatientVisitTotals() {
    if (this.totalCptCounts?.establishedPatientVisit) {
      this.updateTotalEstablishedPatientsCptSummaries();
      this.establishedPatientsMaxValue = this.calculateMaxYValue(this.totalEstablishedPatientsCptSummaries);
    }
  }

  private processSpecialtyNewPatientVisitTotals() {
    if (this.specialtyCptCounts?.newPatientVisit) {
      this.updateSpecialtyTotalNewPatientsCptSummaries();
      this.specialtyNewPatientsMaxValue = this.calculateMaxYValue(this.specialtyTotalNewPatientsCptSummaries);
    }
  }

  private processNewPatientVisitTotals() {
    if (this.totalCptCounts?.newPatientVisit) {
      this.updateTotalNewPatientsCptSummaries();
      this.newPatientsMaxValue = this.calculateMaxYValue(this.totalNewPatientsCptSummaries);
    }
  }

  private setYAxesMaxValues() {
    this.maxNewPatientYAxisValue = Math.max(this.newPatientsMaxValue, this.specialtyNewPatientsMaxValue);
    this.maxEstablishedPatientPatientYAxisValue = Math.max(this.establishedPatientsMaxValue, this.specialtyEstablishedPatientsMaxValue);
    this.maxConsultationsYAxisValue = Math.max(this.consultationsMaxValue, this.specialtyConsultationsMaxValue);
  }

  private calculateMaxYValue(summaries: (CptSummary | undefined)[]) {
    return this.showExtendedBenchmarkOptions ?
      getMaxYaxisValueForEAndMGraphWithExtendedBenchmarkOptions(summaries, this.benchmarkOption) :
      getMaxYaxisValueForEAndMGraphWithOriginalBenchmarkOptions(summaries, this.viewCommunityBenchmarks);
  }

  private updateSpecialtyTotalConsultationsCptSummaries() {
    this.specialtyTotalConsultationsCptSummaries = [
      this.specialtyCptCounts?.consultation.cpt99241,
      this.specialtyCptCounts?.consultation.cpt99242,
      this.specialtyCptCounts?.consultation.cpt99243,
      this.specialtyCptCounts?.consultation.cpt99244,
      this.specialtyCptCounts?.consultation.cpt99245,
    ];
  }

  private updateTotalConsultationsCptSummaries() {
    this.totalConsultationsCptSummaries = [
      this.totalCptCounts?.consultation.cpt99241,
      this.totalCptCounts?.consultation.cpt99242,
      this.totalCptCounts?.consultation.cpt99243,
      this.totalCptCounts?.consultation.cpt99244,
      this.totalCptCounts?.consultation.cpt99245,
    ];
  }

  private updateSpecialtyTotalEstablishedPatientsCptSummaries() {
    this.specialtyTotalEstablishedPatientsCptSummaries = [
      this.specialtyCptCounts?.establishedPatientVisit.cpt99211,
      this.specialtyCptCounts?.establishedPatientVisit.cpt99212,
      this.specialtyCptCounts?.establishedPatientVisit.cpt99213,
      this.specialtyCptCounts?.establishedPatientVisit.cpt99214,
      this.specialtyCptCounts?.establishedPatientVisit.cpt99215,
    ];
  }

  private updateTotalEstablishedPatientsCptSummaries() {
    this.totalEstablishedPatientsCptSummaries = [
      this.totalCptCounts?.establishedPatientVisit.cpt99211,
      this.totalCptCounts?.establishedPatientVisit.cpt99212,
      this.totalCptCounts?.establishedPatientVisit.cpt99213,
      this.totalCptCounts?.establishedPatientVisit.cpt99214,
      this.totalCptCounts?.establishedPatientVisit.cpt99215,
    ];
  }

  private updateSpecialtyTotalNewPatientsCptSummaries() {
    this.specialtyTotalNewPatientsCptSummaries = [
      this.specialtyCptCounts?.newPatientVisit.cpt99201,
      this.specialtyCptCounts?.newPatientVisit.cpt99202,
      this.specialtyCptCounts?.newPatientVisit.cpt99203,
      this.specialtyCptCounts?.newPatientVisit.cpt99204,
      this.specialtyCptCounts?.newPatientVisit.cpt99205,
    ];
  }

  private updateTotalNewPatientsCptSummaries() {
    this.totalNewPatientsCptSummaries = [
      this.totalCptCounts?.newPatientVisit.cpt99201,
      this.totalCptCounts?.newPatientVisit.cpt99202,
      this.totalCptCounts?.newPatientVisit.cpt99203,
      this.totalCptCounts?.newPatientVisit.cpt99204,
      this.totalCptCounts?.newPatientVisit.cpt99205,
    ];
  }

  private createBlankSpecialtyCptCounts() {
    this.specialtyTotalNewPatientsCptSummaries = [];
    this.specialtyTotalEstablishedPatientsCptSummaries = [];
    this.specialtyTotalConsultationsCptSummaries = [];
    this.newPatientVisitsCategories.forEach(x => {
      this.specialtyTotalNewPatientsCptSummaries.push(getDefaultCptSummary());
    });

    this.establishedPatientVisitsCategories.forEach(x => {
      this.specialtyTotalEstablishedPatientsCptSummaries.push(getDefaultCptSummary());
    });

    this.consultationsCategories.forEach(x => {
      this.specialtyTotalConsultationsCptSummaries.push(getDefaultCptSummary());
    });

    this.specialtyNewPatientsMaxValue = 0;
    this.specialtyEstablishedPatientsMaxValue = 0;
    this.consultationsMaxValue = 0;
  }

  private createBlankCptSummaries() {
    this.totalNewPatientsCptSummaries = [];
    this.totalEstablishedPatientsCptSummaries = [];
    this.totalConsultationsCptSummaries = [];
    this.newPatientVisitsCategories.forEach(x => {
      this.totalNewPatientsCptSummaries.push(getDefaultCptSummary());
    });

    this.establishedPatientVisitsCategories.forEach(x => {
      this.totalEstablishedPatientsCptSummaries.push(getDefaultCptSummary());
    });

    this.consultationsCategories.forEach(x => {
      this.totalConsultationsCptSummaries.push(getDefaultCptSummary());
    });
  }
}
